import { Badge, Button, cn } from '@design-system';
import {
  CircularProgress,
  useAnimationPhases,
  WomanEarlyAssistantIllustration,
  WomanLateAssistantIllustration,
  ManEarlyAssistantIllustration,
  ManLateAssistantIllustration,
} from '@/features/client-onboarding';

import { RotateCcw, SkipForward } from 'lucide-react';

import { formatDurationYears } from '@shared';
import { pronoun } from '@client/util';
import { useEffect, useRef, useState } from 'react';

import type { ReactNode } from 'react';

interface CareIntensityPhase {
  id: string;
  duration: number;
}

const PHASES: readonly CareIntensityPhase[] = [
  { id: 'early_care', duration: 5000 },
  { id: 'duration', duration: 5000 },
  { id: 'late_care', duration: 5000 },
] as const;

interface CareIntensityAnimationProps {
  clientFirstName: string;
  lengthOfCareYears: number;
  initialHoursPerWeek: number;
  laterHoursPerWeek: number;
  intakeSurvey: IntakeSurvey;
  isDurationEdited?: boolean;
}

function IllustrationCard({
  title,
  description,
  value,
  children,
  isVisible,
  isActive,
}: {
  title: string;
  description: string;
  value: number;
  children: ReactNode;
  isVisible: boolean;
  isActive: boolean;
}) {
  return (
    <div
      className={cn(
        'z-10 flex translate-y-8 flex-col items-center opacity-0 transition-all duration-700 ease-out',
        {
          'translate-y-0 opacity-100': isVisible,
        },
      )}
    >
      <div
        className={cn(
          'bg-card relative w-80 overflow-hidden rounded-xl border transition-all duration-500',
          { 'border-primary': isActive },
        )}
      >
        <div className="bg-linear-to-b from-muted to-background flex items-center justify-center p-4">
          {children}
        </div>
        <div className="flex flex-col gap-2 p-4 pt-0">
          <div>
            <h3
              className={cn(
                'text-foreground text-lg font-semibold transition-colors duration-300',
                { 'text-primary': isActive },
              )}
            >
              {title}
            </h3>
            <p className="text-muted-foreground text-sm">{description}</p>
          </div>
          <div className="bg-muted w-full rounded-md p-2 text-center">
            <span className="text-primary text-3xl font-bold">{Math.round(value)}</span>
            <span className="text-muted-foreground ml-0.5 text-sm font-medium">hrs/week</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CareIntensityAnimation({
  clientFirstName,
  lengthOfCareYears,
  initialHoursPerWeek,
  laterHoursPerWeek,
  intakeSurvey,
  isDurationEdited,
}: CareIntensityAnimationProps) {
  const { currentPhaseIndex, currentPhase, isComplete, replay, skip, isAutoPlaying } =
    useAnimationPhases({ phases: PHASES });

  const showEarlyCare = currentPhaseIndex >= 0;
  const showDuration = currentPhaseIndex >= 1;
  const showLateCare = currentPhaseIndex >= 2;

  const isMale = pronoun(intakeSurvey) === 'he';
  const EarlyIllustration = isMale
    ? ManEarlyAssistantIllustration
    : WomanEarlyAssistantIllustration;
  const LateIllustration = isMale ? ManLateAssistantIllustration : WomanLateAssistantIllustration;

  const prevPhaseIndex = useRef(currentPhaseIndex);
  const [exitingIndex, setExitingIndex] = useState<number | null>(null);

  useEffect(() => {
    if (currentPhaseIndex !== prevPhaseIndex.current) {
      setExitingIndex(prevPhaseIndex.current);
      const timer = setTimeout(() => setExitingIndex(null), 400);
      prevPhaseIndex.current = currentPhaseIndex;
      return () => clearTimeout(timer);
    }
  }, [currentPhaseIndex]);

  const narratives: Record<string, string> = {
    early_care: `${clientFirstName}'s care journey begins with lighter assistance needs, such as help with household tasks or transportation.`,
    duration: `Care needs evolve over time, typically increasing in intensity as health conditions progress. We predict this progression will happen over ${formatDurationYears(lengthOfCareYears)} years.`,
    late_care: `Later stages may require more intensive support for daily activities like bathing, dressing, and mobility.`,
  };

  const mobilePhases = [
    {
      id: 'early_care',
      title: 'Early Care',
      description: 'Initial assistance needs',
      value: initialHoursPerWeek,
      Illustration: EarlyIllustration,
    },
    {
      id: 'duration',
      title: 'Length of Care',
      description: `${formatDurationYears(lengthOfCareYears)} years`,
      value: null,
      Illustration: null,
    },
    {
      id: 'late_care',
      title: 'Later Care',
      description: 'Intensive support needs',
      value: laterHoursPerWeek,
      Illustration: LateIllustration,
    },
  ];

  return (
    <div className="flex flex-col items-center pb-8 pt-4">
      <div className="mb-6 flex w-full max-w-5xl items-center justify-between gap-4">
        <p
          key={currentPhase.id}
          className="animate-fade-in text-foreground max-w-3xl text-balance text-sm leading-relaxed lg:text-base"
        >
          {narratives[currentPhase.id]}
        </p>
        <CircularProgress
          currentIndex={currentPhaseIndex}
          totalSteps={PHASES.length}
          duration={PHASES[currentPhaseIndex].duration}
        />
      </div>

      {/* Desktop: horizontal layout */}
      <div className="hidden w-full max-w-5xl lg:block">
        <div className="relative flex items-center justify-center">
          <IllustrationCard
            title="Early Care"
            description="Initial assistance needs"
            value={initialHoursPerWeek}
            isVisible={showEarlyCare}
            isActive={currentPhase.id === 'early_care'}
          >
            <EarlyIllustration
              className={cn('size-full text-gray-950', {
                'text-primary-dark': currentPhase.id === 'early_care',
              })}
            />
          </IllustrationCard>

          <div className="z-0 flex flex-col items-center">
            <div
              className={cn(
                'mb-4 translate-y-6 scale-90 text-center opacity-0 transition-all delay-150 duration-700',
                {
                  'translate-y-0 scale-100 opacity-100': showDuration,
                },
              )}
            >
              <p className="text-foreground mb-2 text-xs font-medium uppercase tracking-wider">
                Length of Care
              </p>
              <p className="text-primary text-5xl font-bold">
                {formatDurationYears(lengthOfCareYears)}
              </p>
              <p className="text-foreground flex items-center justify-center gap-1.5 text-sm font-medium">
                years
                {isDurationEdited && <Badge variant="secondary">Edited</Badge>}
              </p>
            </div>

            <div
              className={cn(
                'bg-linear-to-r from-border to-primary h-px w-64 origin-left scale-x-0 opacity-0 transition-all duration-700',
                {
                  'scale-x-100 opacity-100': showDuration,
                },
              )}
            ></div>
          </div>

          <IllustrationCard
            title="Later Care"
            description="Intensive support needs"
            value={laterHoursPerWeek}
            isVisible={showLateCare}
            isActive={currentPhase.id === 'late_care'}
          >
            <LateIllustration
              className={cn('size-full text-gray-950', {
                'text-primary-dark': currentPhase.id === 'late_care',
              })}
            />
          </IllustrationCard>
        </div>
      </div>

      {/* Mobile: one phase at a time with fade-scale transitions */}
      <div className="relative w-full max-w-sm lg:hidden">
        <div className="relative">
          {mobilePhases.map((phase, index) => {
            const isActive = index === currentPhaseIndex;
            const isExiting = index === exitingIndex;

            if (!isActive && !isExiting) return null;

            return (
              <div
                key={phase.id}
                className={cn({
                  'animate-fade-scale-in': isActive,
                  'animate-fade-scale-out pointer-events-none absolute inset-0': isExiting,
                })}
              >
                {phase.Illustration && phase.value != null ? (
                  <IllustrationCard
                    title={phase.title}
                    description={phase.description}
                    value={phase.value}
                    isVisible
                    isActive
                  >
                    <phase.Illustration
                      className={cn('size-full text-gray-950', {
                        'text-primary-dark': isActive,
                      })}
                    />
                  </IllustrationCard>
                ) : (
                  <div className="min-h-111 flex items-center justify-center">
                    <div className="bg-card flex w-full flex-col items-center gap-1 rounded-xl border p-8">
                      <p className="text-foreground text-xs font-medium uppercase tracking-wider">
                        Length of Care
                      </p>
                      <p className="text-primary text-5xl font-bold">
                        {formatDurationYears(lengthOfCareYears)}
                      </p>
                      <p className="text-foreground flex items-center justify-center gap-1.5 text-sm font-medium">
                        years
                        {isDurationEdited && <Badge variant="secondary">Edited</Badge>}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-8 flex w-full max-w-sm items-center justify-end gap-4 lg:mt-12 lg:max-w-5xl">
        {isAutoPlaying && (
          <Button
            size="sm"
            variant="outline"
            onClick={skip}
          >
            <SkipForward />
            Skip
          </Button>
        )}
        {isComplete && (
          <Button
            size="sm"
            variant="outline"
            onClick={replay}
          >
            <RotateCcw />
            Replay
          </Button>
        )}
      </div>
    </div>
  );
}
